import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  CreditCard, 
  Shield, 
  Bell, 
  Palette,
  Sun,
  Moon,
  Monitor,
  Save,
  Lock,
  Sparkles,
  Loader2,
  Check,
  X,
  LayoutDashboard,
  Cloud,
  FileText,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PortalEinstellungen = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL parameter, default to 'uebersicht'
  const initialTab = searchParams.get('tab') || 'uebersicht';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };
  
  // Sync tab with URL params on mount and when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  // Loading states
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const [isLoadingAddress, setIsLoadingAddress] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'system';
    }
    return 'system';
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    email_orders: true,
    email_invoices: true,
    email_updates: false,
    email_marketing: false,
    statusdeck_subscribed: false,
  });
  const [togglingStatusdeck, setTogglingStatusdeck] = useState(false);
  const [togglingNewsletter, setTogglingNewsletter] = useState(false);

  // Profile form
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  // Billing address form
  const [billingAddress, setBillingAddress] = useState({
    company: '',
    first_name: '',
    last_name: '',
    phone: '',
    street: '',
    zip_code: '',
    city: '',
    country: 'DE',
    billing_email: ''
  });

  // Payment method state
  const [paymentInfo, setPaymentInfo] = useState({
    has_subscription: false,
    payment_method: null,
    monthly_price: 0,
    stripe: null,
    paypal: null
  });

  // Password change dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Load payment method from backend
  const loadPaymentMethod = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsLoadingPayment(true);
      const response = await fetch(`${BACKEND_URL}/api/user/payment-method`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentInfo(data);
      }
    } catch (error) {
      console.error('Error loading payment method:', error);
    } finally {
      setIsLoadingPayment(false);
    }
  }, [token]);

  // Check for setup return parameters on mount (once only)
  const setupProcessed = React.useRef(false);
  useEffect(() => {
    if (setupProcessed.current || !token) return;
    
    const setupStatus = searchParams.get('setup');
    const provider = searchParams.get('provider');
    const sessionId = searchParams.get('session_id');
    
    if (!setupStatus) return;
    
    setupProcessed.current = true;
    
    if (setupStatus === 'success' && provider) {
      const confirmSetup = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/user/payment-method/confirm-setup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              provider,
              session_id: sessionId
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            toast.success(data.message || 'Zahlungsmethode erfolgreich eingerichtet!');
            loadPaymentMethod();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Fehler bei der Bestätigung');
          }
        } catch (error) {
          console.error('Error confirming setup:', error);
          toast.error('Verbindungsfehler');
        }
        
        // Clear URL params without triggering re-render loop
        window.history.replaceState({}, '', `${window.location.pathname}?tab=zahlung`);
      };
      
      confirmSetup();
    } else if (setupStatus === 'cancelled') {
      toast.error('Einrichtung abgebrochen');
      window.history.replaceState({}, '', `${window.location.pathname}?tab=zahlung`);
    }
  }, [token]);

  // Load billing address from backend
  const loadBillingAddress = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsLoadingAddress(true);
      const response = await fetch(`${BACKEND_URL}/api/user/billing-address`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingAddress(data);
      }
    } catch (error) {
      console.error('Error loading billing address:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  }, [token]);

  // Load notification preferences from backend
  const loadNotificationPrefs = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsLoadingNotifications(true);
      const response = await fetch(`${BACKEND_URL}/api/user/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error loading notification prefs:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [token]);

  // Load preferences on mount
  useEffect(() => {
    loadNotificationPrefs();
    loadPaymentMethod();
    loadBillingAddress();
  }, [loadNotificationPrefs, loadPaymentMethod, loadBillingAddress]);

  // Update profile from user context when available
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Apply theme
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    toast.success(`Theme geändert auf ${newTheme === 'light' ? 'Hell' : newTheme === 'dark' ? 'Dunkel' : 'System'}!`);
  };

  const handleSaveProfile = async () => {
    if (!token) return;
    
    if (!profile.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    
    setIsSavingProfile(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: profile.name.trim() })
      });
      
      if (response.ok) {
        toast.success('Profil erfolgreich gespeichert! ✨');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Verbindungsfehler beim Speichern');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveBillingAddress = async () => {
    if (!token) return;
    
    // Validation
    if (!billingAddress.first_name.trim() || !billingAddress.last_name.trim()) {
      toast.error('Vor- und Nachname sind erforderlich');
      return;
    }
    if (!billingAddress.street.trim()) {
      toast.error('Straße ist erforderlich');
      return;
    }
    if (!billingAddress.zip_code.trim() || !billingAddress.city.trim()) {
      toast.error('PLZ und Stadt sind erforderlich');
      return;
    }
    
    setIsSavingAddress(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/billing-address`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(billingAddress)
      });
      
      if (response.ok) {
        toast.success('Rechnungsadresse gespeichert! 📬');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving billing address:', error);
      toast.error('Verbindungsfehler beim Speichern');
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!token) return;
    
    setIsSavingNotifications(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notifications)
      });
      
      if (response.ok) {
        toast.success('Benachrichtigungseinstellungen gespeichert! 📬');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving notifications:', error);
      toast.error('Verbindungsfehler beim Speichern');
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleToggleStatusdeck = async (subscribe) => {
    if (!token) return;
    setTogglingStatusdeck(true);
    
    try {
      const endpoint = subscribe ? 'subscribe' : 'unsubscribe';
      const response = await fetch(`${BACKEND_URL}/api/user/statusdeck/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setNotifications(prev => ({ ...prev, statusdeck_subscribed: subscribe }));
        toast.success(subscribe ? 'Status-Meldungen abonniert' : 'Status-Meldungen abbestellt');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern');
      }
    } catch (error) {
      console.error('Error toggling statusdeck:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setTogglingStatusdeck(false);
    }
  };

  const handleToggleNewsletter = async (subscribe) => {
    if (!token) return;
    setTogglingNewsletter(true);
    
    try {
      const endpoint = subscribe ? 'subscribe' : 'unsubscribe';
      const response = await fetch(`${BACKEND_URL}/api/user/newsletter/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setNotifications(prev => ({ ...prev, newsletter_subscribed: subscribe }));
        toast.success(subscribe ? 'Newsletter abonniert' : 'Newsletter abbestellt');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern');
      }
    } catch (error) {
      console.error('Error toggling newsletter:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setTogglingNewsletter(false);
    }
  };

  const handleChangePaymentMethod = async (newMethod) => {
    if (!token) return;
    if (newMethod === paymentInfo.active_method) return;
    
    setIsSavingPayment(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/payment-method`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          payment_method: newMethod,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.requires_setup) {
          // Redirect to payment provider for setup
          toast.info(data.message || 'Weiterleitung zur Zahlungseinrichtung...');
          window.location.href = data.setup_url;
        } else {
          // Direct switch successful
          setPaymentInfo(prev => ({ ...prev, active_method: newMethod }));
          toast.success(data.message || 'Zahlungsmethode geändert! 💳');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern der Zahlungsmethode');
      }
    } catch (error) {
      console.error('Error changing payment method:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleChangePassword = async () => {
    if (!token) return;
    
    // Check if user has existing password
    const hasPassword = user?.has_password;
    
    // Validation
    if (hasPassword && !passwordForm.current_password) {
      toast.error('Bitte aktuelles Passwort eingeben');
      return;
    }
    
    if (!passwordForm.new_password) {
      toast.error('Bitte neues Passwort eingeben');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    setIsLoadingPassword(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: hasPassword ? passwordForm.current_password : undefined,
          new_password: passwordForm.new_password
        })
      });
      
      if (response.ok) {
        const action = hasPassword ? 'geändert' : 'gesetzt';
        toast.success(`Passwort erfolgreich ${action}! 🔐`);
        setShowPasswordDialog(false);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Verbindungsfehler beim Ändern des Passworts');
    } finally {
      setIsLoadingPassword(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-einstellungen">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-lg shadow-primary/30">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold">
            Einstellungen
          </h1>
          <p className="text-sm text-muted-foreground">
            Verwalte dein Helden-Konto und deine Präferenzen
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="uebersicht" className="gap-2" data-testid="tab-uebersicht">
            <LayoutDashboard className="h-4 w-4 hidden sm:inline" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="profil" className="gap-2" data-testid="tab-profil">
            <User className="h-4 w-4 hidden sm:inline" />
            Profil
          </TabsTrigger>
          <TabsTrigger value="zahlung" className="gap-2" data-testid="tab-zahlung">
            <CreditCard className="h-4 w-4 hidden sm:inline" />
            Zahlung
          </TabsTrigger>
          <TabsTrigger value="sicherheit" className="gap-2" data-testid="tab-sicherheit">
            <Shield className="h-4 w-4 hidden sm:inline" />
            Sicherheit
          </TabsTrigger>
          <TabsTrigger value="benachrichtigungen" className="gap-2" data-testid="tab-benachrichtigungen">
            <Bell className="h-4 w-4 hidden sm:inline" />
            Mitteilungen
          </TabsTrigger>
        </TabsList>

        {/* Tab: Übersicht */}
        <TabsContent value="uebersicht" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Angemeldet als</p>
                    <p className="font-medium truncate">{user?.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <CreditCard className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Zahlungsmethode</p>
                    <p className="font-medium">
                      {paymentInfo.has_subscription 
                        ? (paymentInfo.payment_method === 'stripe' ? 'Kreditkarte' : 'PayPal')
                        : 'Nicht eingerichtet'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Palette className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Erscheinungsbild</p>
                    <p className="font-medium">
                      {theme === 'light' ? 'Hell' : theme === 'dark' ? 'Dunkel' : 'System'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Appearance / Theme */}
          <Card className="glass-card border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                Erscheinungsbild
              </CardTitle>
              <CardDescription>
                Wähle dein bevorzugtes Theme für das Helden-Portal.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <button
                  onClick={() => handleThemeChange('light')}
                  className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    theme === 'light' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="theme-light"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <Sun className="h-6 w-6" />
                  </div>
                  <span className="font-medium">Hell</span>
                  {theme === 'light' && <Badge className="bg-primary">Aktiv</Badge>}
                </button>

                <button
                  onClick={() => handleThemeChange('dark')}
                  className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    theme === 'dark' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="theme-dark"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800 text-slate-200">
                    <Moon className="h-6 w-6" />
                  </div>
                  <span className="font-medium">Dunkel</span>
                  {theme === 'dark' && <Badge className="bg-primary">Aktiv</Badge>}
                </button>

                <button
                  onClick={() => handleThemeChange('system')}
                  className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    theme === 'system' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="theme-system"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-slate-800 text-white">
                    <Monitor className="h-6 w-6" />
                  </div>
                  <span className="font-medium">System</span>
                  {theme === 'system' && <Badge className="bg-primary">Aktiv</Badge>}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Schnellzugriff</CardTitle>
              <CardDescription>
                Häufig verwendete Einstellungen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => handleTabChange('zahlung')}
                >
                  <CreditCard className="mr-3 h-5 w-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">Zahlungseinstellungen</p>
                    <p className="text-xs text-muted-foreground">Zahlungsmethode ändern</p>
                  </div>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => {
                    handleTabChange('sicherheit');
                    setTimeout(() => setShowPasswordDialog(true), 100);
                  }}
                >
                  <Lock className="mr-3 h-5 w-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">Passwort ändern</p>
                    <p className="text-xs text-muted-foreground">Konto-Sicherheit verwalten</p>
                  </div>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => handleTabChange('profil')}
                >
                  <User className="mr-3 h-5 w-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">Profil bearbeiten</p>
                    <p className="text-xs text-muted-foreground">Name und Infos ändern</p>
                  </div>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => handleTabChange('benachrichtigungen')}
                >
                  <Bell className="mr-3 h-5 w-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">Benachrichtigungen</p>
                    <p className="text-xs text-muted-foreground">E-Mail-Einstellungen</p>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Profil */}
        <TabsContent value="profil" className="space-y-6">
          {/* Benutzerkonto */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Benutzerkonto
              </CardTitle>
              <CardDescription>
                Deine Login-Daten für das Helden-Portal. Mit diesen Daten meldest du dich an.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-blue-500/5 border border-blue-500/20 p-3 text-sm">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Was ist das Benutzerkonto?</strong><br />
                  Das sind deine Account-Daten für die Anmeldung im Helden-Portal. 
                  Der Anzeigename wird z.B. oben rechts im Menü angezeigt.
                </p>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Anzeigename</Label>
                  <Input
                    id="name"
                    data-testid="profile-name-input"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Dein Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail (Login)</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="profile-email-input"
                    value={profile.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Die Login-E-Mail kann nicht geändert werden.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleSaveProfile} 
                disabled={isSavingProfile}
                className="bg-gradient-to-r from-primary to-blue-600"
                data-testid="save-profile-btn"
              >
                {isSavingProfile ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Benutzerkonto speichern
              </Button>
            </CardContent>
          </Card>

          {/* Rechnungsprofil / Kundendaten */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Rechnungsprofil
              </CardTitle>
              <CardDescription>
                Deine Kundendaten für Rechnungen und geschäftliche Korrespondenz.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 text-sm">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Was ist das Rechnungsprofil?</strong><br />
                  Diese Daten erscheinen auf deinen Rechnungen. Hier kannst du auch Firmendaten 
                  hinterlegen, falls du die Cloud geschäftlich nutzt. Die Adresse wird für den 
                  Rechnungsversand und rechtliche Dokumente verwendet.
                </p>
              </div>
              
              {isLoadingAddress ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company">Firma (optional)</Label>
                    <Input
                      id="company"
                      data-testid="billing-company-input"
                      value={billingAddress.company}
                      onChange={(e) => setBillingAddress({ ...billingAddress, company: e.target.value })}
                      placeholder="Firmenname"
                    />
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname *</Label>
                      <Input
                        id="first_name"
                        data-testid="billing-firstname-input"
                        value={billingAddress.first_name}
                        onChange={(e) => setBillingAddress({ ...billingAddress, first_name: e.target.value })}
                        placeholder="Max"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname *</Label>
                      <Input
                        id="last_name"
                        data-testid="billing-lastname-input"
                        value={billingAddress.last_name}
                        onChange={(e) => setBillingAddress({ ...billingAddress, last_name: e.target.value })}
                        placeholder="Mustermann"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon (optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="billing-phone-input"
                      value={billingAddress.phone || ''}
                      onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                      placeholder="+49 30 123456"
                    />
                    <p className="text-xs text-muted-foreground">
                      Wird für Domain-Registrierungen (WHOIS) benötigt
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="billing_email">Rechnungs-E-Mail</Label>
                    <Input
                      id="billing_email"
                      type="email"
                      data-testid="billing-email-input"
                      value={billingAddress.billing_email || ''}
                      onChange={(e) => setBillingAddress({ ...billingAddress, billing_email: e.target.value })}
                      placeholder="buchhaltung@firma.de"
                    />
                    <p className="text-xs text-muted-foreground">
                      An diese Adresse werden Rechnungen gesendet. Leer lassen für Login-E-Mail.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="street">Straße und Hausnummer *</Label>
                    <Input
                      id="street"
                      data-testid="billing-street-input"
                      value={billingAddress.street}
                      onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
                      placeholder="Musterstraße 123"
                    />
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">PLZ *</Label>
                      <Input
                        id="zip_code"
                        data-testid="billing-zip-input"
                        value={billingAddress.zip_code}
                        onChange={(e) => setBillingAddress({ ...billingAddress, zip_code: e.target.value })}
                        placeholder="12345"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="city">Stadt *</Label>
                      <Input
                        id="city"
                        data-testid="billing-city-input"
                        value={billingAddress.city}
                        onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                        placeholder="Berlin"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="country">Land</Label>
                    <Input
                      id="country"
                      data-testid="billing-country-input"
                      value={billingAddress.country === 'DE' ? 'Deutschland' : billingAddress.country}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Aktuell nur Deutschland verfügbar.
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleSaveBillingAddress} 
                    disabled={isSavingAddress}
                    className="bg-gradient-to-r from-primary to-blue-600"
                    data-testid="save-billing-btn"
                  >
                    {isSavingAddress ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Rechnungsprofil speichern
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Zahlung */}
        <TabsContent value="zahlung" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Zahlungseinstellungen
              </CardTitle>
              <CardDescription>
                Deine Zahlungsmethode für das Helden-Abo. Monatliche Abbuchung.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingPayment ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : paymentInfo.has_subscription ? (
                <>
                  {/* Current payment method info */}
                  <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-sm">
                    <div className="flex items-start gap-2">
                      <Shield className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Aktive Einzugsermächtigung</p>
                        <p className="text-muted-foreground mt-1">
                          Monatliche Abbuchung von <strong>{paymentInfo.monthly_price?.toFixed(2)} €</strong> über <strong>{paymentInfo.active_method === 'stripe' ? 'Kreditkarte' : 'PayPal'}</strong>.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Payment method selection */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium">Zahlungsmethode wechseln</h4>
                    <p className="text-xs text-muted-foreground">Die nächste Rechnung wird dann über die neue Methode eingezogen.</p>
                    
                    {/* Stripe / Kreditkarte */}
                    <button
                      onClick={() => handleChangePaymentMethod('stripe')}
                      disabled={isSavingPayment}
                      className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                        paymentInfo.active_method === 'stripe'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="payment-method-stripe"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#635BFF]/10">
                          <CreditCard className="h-5 w-5 text-[#635BFF]" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Kreditkarte</p>
                          {paymentInfo.stripe?.is_stored ? (
                            <p className="text-xs text-muted-foreground">
                              {paymentInfo.stripe.brand} •••• {paymentInfo.stripe.last_four}
                              {paymentInfo.stripe.card_holder_name && ` · ${paymentInfo.stripe.card_holder_name}`}
                              {paymentInfo.stripe.expires_month && paymentInfo.stripe.expires_year && 
                                ` · Gültig bis ${paymentInfo.stripe.expires_month}/${paymentInfo.stripe.expires_year}`}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Noch nicht hinterlegt – Klicken zum Einrichten
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {paymentInfo.stripe?.is_stored && (
                          <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                            <Check className="h-3 w-3 mr-1" />
                            Hinterlegt
                          </Badge>
                        )}
                        {paymentInfo.active_method === 'stripe' && (
                          <Badge className="bg-primary">Aktiv</Badge>
                        )}
                      </div>
                    </button>

                    {/* PayPal */}
                    <button
                      onClick={() => handleChangePaymentMethod('paypal')}
                      disabled={isSavingPayment}
                      className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                        paymentInfo.active_method === 'paypal'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="payment-method-paypal"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#003087]/10">
                          <span className="text-[#003087] font-bold text-sm">PP</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">PayPal</p>
                          {paymentInfo.paypal?.is_stored ? (
                            <p className="text-xs text-muted-foreground">
                              {paymentInfo.paypal.email}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Noch nicht verknüpft – Klicken zum Einrichten
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {paymentInfo.paypal?.is_stored && (
                          <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                            <Check className="h-3 w-3 mr-1" />
                            Hinterlegt
                          </Badge>
                        )}
                        {paymentInfo.active_method === 'paypal' && (
                          <Badge className="bg-primary">Aktiv</Badge>
                        )}
                      </div>
                    </button>

                    {isSavingPayment && (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Wird geändert...
                      </div>
                    )}
                  </div>
                  
                  {/* Info box */}
                  <div className="rounded-lg bg-muted/50 p-4 text-sm">
                    <p className="text-muted-foreground">
                      <strong>Hinweis:</strong> Um die Zahlungsmethode zu wechseln, muss eine gültige Einzugsermächtigung für den gewählten Anbieter vorliegen. 
                      Falls noch nicht vorhanden, werden Sie zur Einrichtung weitergeleitet.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* No subscription yet */}
                  <div className="rounded-lg bg-muted/50 border border-dashed p-6 text-center">
                    <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
                    <p className="font-medium mb-1">Noch kein aktives Abo</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Nach deiner ersten Bestellung kannst du hier deine Zahlungsmethode verwalten.
                    </p>
                  </div>

                  {/* Accepted methods info */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium">Akzeptierte Zahlungsmethoden</h4>
                    
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#635BFF]/10">
                        <CreditCard className="h-5 w-5 text-[#635BFF]" />
                      </div>
                      <div>
                        <p className="font-medium">Kreditkarte</p>
                        <p className="text-xs text-muted-foreground">Visa, Mastercard, American Express via Stripe</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#003087]/10">
                        <span className="text-[#003087] font-bold text-sm">PP</span>
                      </div>
                      <div>
                        <p className="font-medium">PayPal</p>
                        <p className="text-xs text-muted-foreground">Direkt mit deinem PayPal-Konto</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Sicherheit */}
        <TabsContent value="sicherheit" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Sicherheit
              </CardTitle>
              <CardDescription>
                Schütze dein Helden-Konto.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Passwort ändern</p>
                    <p className="text-sm text-muted-foreground">
                      Zuletzt geändert: Noch nie
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowPasswordDialog(true)}
                  data-testid="change-password-btn"
                >
                  Ändern
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Magic Link Login</p>
                    <p className="text-sm text-muted-foreground">
                      Anmeldung per E-Mail-Link ohne Passwort
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                  Aktiv
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Benachrichtigungen */}
        <TabsContent value="benachrichtigungen" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Benachrichtigungen
              </CardTitle>
              <CardDescription>
                Verwalte deine E-Mail-Benachrichtigungen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingNotifications ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Infrastruktur Status-Meldungen</Label>
                        <p className="text-sm text-muted-foreground">
                          E-Mail bei Störungen, geplanten Wartungen und Statusänderungen
                        </p>
                      </div>
                      <Switch
                        data-testid="notif-statusdeck-switch"
                        checked={notifications.statusdeck_subscribed}
                        disabled={togglingStatusdeck}
                        onCheckedChange={(checked) => handleToggleStatusdeck(checked)}
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Newsletter</Label>
                        <p className="text-sm text-muted-foreground">
                          Tipps, Tricks und Neuigkeiten von den Wolkenhelden
                        </p>
                      </div>
                      <Switch
                        data-testid="notif-marketing-switch"
                        checked={notifications.newsletter_subscribed}
                        disabled={togglingNewsletter}
                        onCheckedChange={(checked) => handleToggleNewsletter(checked)}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {user?.has_password ? 'Passwort ändern' : 'Passwort setzen'}
            </DialogTitle>
            <DialogDescription>
              {user?.has_password 
                ? 'Gib dein aktuelles Passwort ein und wähle ein neues sicheres Passwort.'
                : 'Du nutzt aktuell nur Magic Link. Setze ein Passwort um dich auch mit E-Mail & Passwort anmelden zu können.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {user?.has_password && (
              <div className="space-y-2">
                <Label htmlFor="current_password">Aktuelles Passwort</Label>
                <Input
                  id="current_password"
                  type="password"
                  data-testid="current-password-input"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="new_password">Neues Passwort</Label>
              <Input
                id="new_password"
                type="password"
                data-testid="new-password-input"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Min. 8 Zeichen"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Passwort bestätigen</Label>
              <Input
                id="confirm_password"
                type="password"
                data-testid="confirm-password-input"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Neues Passwort wiederholen"
              />
              {passwordForm.new_password && passwordForm.confirm_password && (
                <div className="flex items-center gap-1 text-sm">
                  {passwordForm.new_password === passwordForm.confirm_password ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-green-500">Passwörter stimmen überein</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 text-red-500" />
                      <span className="text-red-500">Passwörter stimmen nicht überein</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPasswordDialog(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={isLoadingPassword}
              className="bg-gradient-to-r from-primary to-blue-600"
              data-testid="confirm-password-change-btn"
            >
              {isLoadingPassword ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              Passwort ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
